import type { ComponentChildren } from 'preact'
import { Fragment } from 'preact'
import type { QuizDto, QuizDraftDto } from '@/types/api-generated'
import { describeQuizDraft, dimensionNameFor, questionNumberAfterMerge, type QuizDraftDescription } from '@/utils/quizDraft'

interface Props {
    draft: QuizDraftDto
    /** The editor's working quiz from the chat context, or null when the Quiz field isn't on screen. */
    workingQuiz: QuizDto | null
    /** True only while the Quiz editor of the draft's own web is open. */
    canPutInEditor: boolean
    /** The draft was checked against a different web from the one the chat is on now. */
    isForAnotherWeb: boolean
    /** Set once the draft is in the editor, frozen as it read then: after the merge "Adds question 11" would read "Replaces". */
    appliedDescription: QuizDraftDescription | null
    onPutInEditor: (description: QuizDraftDescription) => Promise<boolean>
}

const BUTTON = 'w-full px-3 py-2 text-xs font-medium rounded-lg'
const BUTTON_GREYED = `${BUTTON} border border-slate-200 bg-slate-50 text-slate-500 cursor-default dark:border-slate-700 dark:bg-slate-900 dark:text-slate-400`

/**
 * Webster's quiz draft under its reply (ORB-926): what it covers, then the items, then Put in editor. A draft of one
 * item shows it in full. Put in editor hands the draft to the Quiz editor, which merges it; nothing is saved.
 */
export function QuizDraftCard({ draft, workingQuiz, canPutInEditor, isForAnotherWeb, appliedDescription, onPutInEditor }: Props) {
    const applied = appliedDescription !== null
    const description = appliedDescription ?? describeQuizDraft(draft, workingQuiz)

    const putInEditor = async () => {
        if (applied || !canPutInEditor) return
        await onPutInEditor(description)
    }

    return (
        <div className="mt-3 rounded-lg border border-slate-200 bg-white overflow-hidden dark:border-slate-700 dark:bg-slate-800">
            <div className="px-3 py-2.5 bg-slate-50 border-b border-slate-200 dark:bg-slate-900 dark:border-slate-700">
                <p className="text-xs">
                    <span className="font-semibold text-slate-900 dark:text-white">Draft</span>
                    <span className="text-slate-400 dark:text-slate-500"> &middot; </span>
                    <span className="font-medium text-slate-700 dark:text-slate-300">{description.scope}</span>
                </p>
                {description.countParts && (
                    <p className="mt-1 text-xs leading-relaxed text-slate-600 dark:text-slate-300">
                        {description.countParts.map((part, index) => (
                            <Fragment key={part}>
                                {index > 0 && <>&nbsp;&middot; </>}
                                <span className="whitespace-nowrap">{part}</span>
                            </Fragment>
                        ))}
                    </p>
                )}
                {description.placement && <p className="mt-1 text-xs leading-relaxed text-slate-600 dark:text-slate-300">{description.placement}</p>}
            </div>

            {description.singleKind ? <SingleItem draft={draft} workingQuiz={workingQuiz} /> : <ItemList draft={draft} workingQuiz={workingQuiz} />}

            <div className="p-2.5 border-t border-slate-200 dark:border-slate-700">
                {applied ? (
                    <button type="button" disabled className={BUTTON_GREYED}>
                        <span className="flex items-center justify-center gap-1">
                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                            In the editor
                        </span>
                    </button>
                ) : canPutInEditor ? (
                    <button type="button" onClick={putInEditor} className={`${BUTTON} bg-primary-600 hover:bg-primary-700 text-white transition-colors`}>Put in editor</button>
                ) : (
                    <>
                        <button type="button" disabled className={BUTTON_GREYED}>Put in editor</button>
                        <p className="mt-1.5 text-center text-[11px] text-slate-500 dark:text-slate-400">{isForAnotherWeb ? 'This draft is for another web' : 'Click Edit on the Quiz tab first'}</p>
                    </>
                )}
            </div>
        </div>
    )
}

function Group({ title, children }: { title: string; children: ComponentChildren }) {
    return (
        <>
            <p className="pt-2.5 first:pt-0 pb-1 text-[10.5px] font-semibold tracking-wider uppercase text-slate-400 dark:text-slate-500">{title}</p>
            <ul>{children}</ul>
        </>
    )
}

function Row({ number, text, aside }: { number?: number | null; text: string; aside?: ComponentChildren }) {
    return (
        <li className="flex items-baseline gap-2 py-[3px]">
            {number !== undefined && <span className="w-5 shrink-0 tabular-nums text-right text-slate-400 dark:text-slate-500">{number ?? ''}</span>}
            <span className="min-w-0 flex-1 truncate text-slate-800 dark:text-slate-200">{text}</span>
            {aside != null && <span className="shrink-0 text-slate-500 dark:text-slate-400">{aside}</span>}
        </li>
    )
}

const tiersOf = (advice: { LowAdvice?: string | null; MidAdvice?: string | null; HighAdvice?: string | null }) =>
    [advice.LowAdvice && 'low', advice.MidAdvice && 'mid', advice.HighAdvice && 'high'].filter(Boolean).join(', ')

/** A draft of several items: each kind as a short list, in the order the count line names them. */
function ItemList({ draft, workingQuiz }: { draft: QuizDraftDto; workingQuiz: QuizDto | null }) {
    return (
        <div className="max-h-[232px] overflow-y-auto px-3 py-2.5 text-xs">
            {draft.Dimensions.length > 0 && (
                <Group title="Dimensions">
                    {draft.Dimensions.map(dimension => (
                        <Row key={dimension.Id} text={dimension.Name} aside={<span className="font-mono text-[11px]">{dimension.Id}</span>} />
                    ))}
                </Group>
            )}
            {draft.Questions.length > 0 && (
                <Group title="Questions">
                    {draft.Questions.map((question, index) => (
                        <Row
                            key={question.Id}
                            number={workingQuiz ? questionNumberAfterMerge(workingQuiz, question.Id).number + appendedBefore(draft, workingQuiz, index) : null}
                            text={question.Text || question.Id}
                        />
                    ))}
                </Group>
            )}
            {draft.Bands.length > 0 && (
                <Group title="Bands">
                    {draft.Bands.map(band => (
                        <Row key={band.Id} text={band.Name || band.Id} aside={<span className="tabular-nums">{band.MinScore}-{band.MaxScore}</span>} />
                    ))}
                </Group>
            )}
            {draft.DimensionAdvice.length > 0 && (
                <Group title="Advice">
                    {draft.DimensionAdvice.map(advice => (
                        <Row key={advice.DimensionId} text={dimensionNameFor(advice.DimensionId, draft, workingQuiz)} aside={tiersOf(advice)} />
                    ))}
                </Group>
            )}
            {draft.ReportSections.length > 0 && (
                <Group title="Report sections">
                    {draft.ReportSections.map(section => <Row key={section.Id} text={section.Title || section.Id} />)}
                </Group>
            )}
            {draft.Disclaimer != null && (
                <Group title="Disclaimer">
                    <Row text={draft.Disclaimer} />
                </Group>
            )}
        </div>
    )
}

/**
 * New questions are appended in draft order, so each new one lands after the new ones before it. A question that
 * replaces one keeps its own number.
 */
function appendedBefore(draft: QuizDraftDto, workingQuiz: QuizDto, index: number): number {
    if (workingQuiz.Questions.some(question => question.Id === draft.Questions[index].Id)) return 0
    return draft.Questions.slice(0, index).filter(earlier => !workingQuiz.Questions.some(question => question.Id === earlier.Id)).length
}

function Detail({ term, children }: { term: string; children: ComponentChildren }) {
    return (
        <>
            <dt className="text-slate-500 pt-px dark:text-slate-400">{term}</dt>
            <dd className="min-w-0 text-slate-800 leading-relaxed dark:text-slate-200">{children}</dd>
        </>
    )
}

const Mono = ({ children }: { children: ComponentChildren }) => <span className="font-mono text-[11px] text-slate-700 dark:text-slate-300">{children}</span>

/** A draft of one item, shown in full. */
function SingleItem({ draft, workingQuiz }: { draft: QuizDraftDto; workingQuiz: QuizDto | null }) {
    const scale = workingQuiz?.ScaleMax
    const question = draft.Questions[0]
    const dimension = draft.Dimensions[0]
    const band = draft.Bands[0]
    const advice = draft.DimensionAdvice[0]
    const section = draft.ReportSections[0]

    return (
        <dl className="px-3 py-3 grid grid-cols-[4.75rem_minmax(0,1fr)] gap-x-3 gap-y-2 text-xs">
            {question && (
                <>
                    <Detail term="Question"><span className="text-sm font-medium leading-snug text-slate-900 dark:text-white">{question.Text}</span></Detail>
                    <Detail term="ID"><Mono>{question.Id}</Mono></Detail>
                    <Detail term="Dimension">{dimensionNameFor(question.DimensionId, draft, workingQuiz)}</Detail>
                    <Detail term="Low (1)">{question.LowLabel}</Detail>
                    <Detail term={scale ? `High (${scale})` : 'High'}>{question.HighLabel}</Detail>
                    {question.HelpText && <Detail term="Help">{question.HelpText}</Detail>}
                    {question.AllowsNotApplicable && <Detail term="Not applicable">{question.NotApplicableLabel || 'Not applicable'}</Detail>}
                    {question.IsReverseScored && <Detail term="Reverse">Reverse scored</Detail>}
                </>
            )}
            {dimension && (
                <>
                    <Detail term="Name">{dimension.Name}</Detail>
                    <Detail term="ID"><Mono>{dimension.Id}</Mono></Detail>
                </>
            )}
            {band && (
                <>
                    <Detail term="Name">{band.Name}</Detail>
                    <Detail term="ID"><Mono>{band.Id}</Mono></Detail>
                    <Detail term="Range"><span className="tabular-nums">{band.MinScore}-{band.MaxScore}</span></Detail>
                    {band.WhatThisMeans && <Detail term="Means">{band.WhatThisMeans}</Detail>}
                    {band.WhatToDo && <Detail term="To do">{band.WhatToDo}</Detail>}
                </>
            )}
            {advice && (
                <>
                    <Detail term="Dimension">{dimensionNameFor(advice.DimensionId, draft, workingQuiz)}</Detail>
                    {advice.LowAdvice && <Detail term="Low">{advice.LowAdvice}</Detail>}
                    {advice.MidAdvice && <Detail term="Mid">{advice.MidAdvice}</Detail>}
                    {advice.HighAdvice && <Detail term="High">{advice.HighAdvice}</Detail>}
                </>
            )}
            {section && (
                <>
                    <Detail term="Title">{section.Title}</Detail>
                    {section.Text && <Detail term="Text">{section.Text}</Detail>}
                </>
            )}
            {draft.Disclaimer != null && <Detail term="Disclaimer">{draft.Disclaimer}</Detail>}
        </dl>
    )
}
